import { Link, type LinkOptions, linkOptions } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { type ComponentProps, type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
	ConnectionItem,
	ConnectorDefinitionItem,
} from "@/lib/api/connections";
import { connectionLabel } from "@/lib/connection-label";
import { connectorIsDeviceBound } from "@/lib/connectors/facets";
import { CapabilitySearch } from "./capability-browser";
import { PageSection } from "./page-section";
import { StatusDot } from "./status-dot";

/** Presentation only: the destination preserves each source's own identity. */
export interface ConnectionListEntry {
	id: string;
	name: string;
	description?: string;
	status?: string;
	tone: ComponentProps<typeof StatusDot>["tone"];
	needsAttention?: boolean;
	link: LinkOptions;
}

interface ConnectionsBrowserProps {
	entries: ConnectionListEntry[];
	action?: ReactNode;
	isLoading?: boolean;
	isError?: boolean;
	onRetry?: () => void;
}

/** One Connections section for accounts, browser profiles, devices and runtimes. */
export function ConnectionsSection(props: ConnectionsBrowserProps) {
	return (
		<PageSection
			title="Connections"
			count={
				props.isLoading || props.isError
					? undefined
					: String(props.entries.length)
			}
		>
			<ConnectionsBrowser {...props} />
		</PageSection>
	);
}

function ConnectionsBrowser({
	entries,
	action,
	isLoading,
	isError,
	onRetry,
}: ConnectionsBrowserProps) {
	const [query, setQuery] = useState("");
	if (isLoading)
		return (
			<p role="status" className="text-sm text-muted-foreground">
				Loading connections…
			</p>
		);
	if (isError)
		return (
			<div role="alert" className="space-y-2">
				<p>Could not load connections.</p>
				{onRetry && (
					<Button variant="outline" size="sm" onClick={onRetry}>
						Retry
					</Button>
				)}
			</div>
		);
	const matching = entries
		.filter((entry) =>
			`${entry.name} ${entry.description ?? ""}`
				.toLowerCase()
				.includes(query.trim().toLowerCase()),
		)
		.sort((a, b) => Number(!!b.needsAttention) - Number(!!a.needsAttention));
	return (
		<div className="space-y-4">
			<CapabilitySearch
				label="connections"
				query={query}
				onQueryChange={setQuery}
				count={matching.length}
				action={action}
			/>
			<ul
				className={
					matching.length > 0
						? "overflow-hidden rounded-lg border divide-y"
						: undefined
				}
			>
				{matching.map((entry) => (
					<li key={entry.id}>
						<Link
							{...entry.link}
							className="flex items-center justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
						>
							<span className="flex min-w-0 flex-1 items-center gap-2">
								<StatusDot tone={entry.tone} />
								<span className="min-w-0">
									<span className="block truncate text-sm font-medium">
										{entry.name}
									</span>
									{entry.description && (
										<span className="block truncate text-xs text-muted-foreground">
											{entry.description}
										</span>
									)}
								</span>
							</span>
							<span className="flex min-w-0 max-w-1/2 shrink-0 items-center gap-2">
								{entry.status && (
									<span className="min-w-0 truncate text-xs text-muted-foreground">
										{entry.status}
									</span>
								)}
								<ChevronRight
									aria-hidden="true"
									className="size-4 shrink-0 text-muted-foreground"
								/>
							</span>
						</Link>
					</li>
				))}
			</ul>
			{matching.length === 0 && (
				<p className="py-5 text-sm text-muted-foreground">
					{query.trim()
						? "No matches. Try another search."
						: "No connections yet."}
				</p>
			)}
		</div>
	);
}

/** One connection's health: dot tone, the problem to show, and whether it sorts first. */
export function connectionHealth(
	connection: ConnectionItem,
	connector?: ConnectorDefinitionItem,
): {
	tone: ComponentProps<typeof StatusDot>["tone"];
	status: string | undefined;
	needsAttention: boolean;
} {
	const needsDevice =
		!!connector &&
		connectorIsDeviceBound(connector) &&
		!connection.device_worker_id;
	const unhealthy = ["error", "revoked", "pending_auth"].includes(
		connection.status,
	);
	return {
		needsAttention: unhealthy || needsDevice,
		tone:
			connection.status === "error" || connection.status === "revoked"
				? "destructive"
				: needsDevice || connection.status !== "active"
					? "amber"
					: null,
		status: needsDevice
			? "Needs a device"
			: unhealthy
				? connection.error_message?.trim() ||
					(connection.status === "pending_auth"
						? "needs reconnect"
						: connection.status)
				: undefined,
	};
}

/** The one "Add connection" control: opens the connector's create form. */
export function AddConnectionButton({
	ownerSlug,
	connectorKey,
	iconOnly = false,
}: {
	ownerSlug: string;
	connectorKey: string;
	/** In a list row: an icon with a tooltip, like the other row actions. */
	iconOnly?: boolean;
}) {
	const link = (
		<Link
			to="/$owner/connectors/create/$connectorKey"
			params={{ owner: ownerSlug, connectorKey }}
		>
			<Plus className={iconOnly ? "h-4 w-4" : "size-3.5"} aria-hidden="true" />
			<span className={iconOnly ? "sr-only" : undefined}>Add connection</span>
		</Link>
	);
	if (!iconOnly)
		return (
			<Button asChild size="sm" variant="outline">
				{link}
			</Button>
		);
	return (
		<TooltipProvider delayDuration={200}>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						asChild
						variant="ghost"
						size="icon"
						className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
					>
						{link}
					</Button>
				</TooltipTrigger>
				<TooltipContent>Add connection</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

/** Keep connection-specific labels, device requirements and reconnect semantics here. */
export function ConnectorConnectionsBrowser({
	connections,
	connector,
	connectorKey,
	ownerSlug,
	section = false,
	action,
}: {
	connections: ConnectionItem[];
	connector?: ConnectorDefinitionItem;
	connectorKey: string;
	ownerSlug: string;
	section?: boolean;
	/** Replaces the plain "Add connection" link, e.g. with the connector page's connect menu. */
	action?: ReactNode;
}) {
	const entries = connections
		.filter((connection) => connection.connector_key === connectorKey)
		.map((connection): ConnectionListEntry => {
			const health = connectionHealth(connection, connector);
			return {
				id: String(connection.id),
				name: connectionLabel({
					...connection,
					connector_name: connection.connector_name ?? connector?.name ?? null,
				}),
				needsAttention: health.needsAttention,
				tone: health.tone,
				status: health.status,
				link: linkOptions({
					to: "/$owner/connectors/$connectorKey/$connectionId",
					params: {
						owner: ownerSlug,
						connectorKey,
						connectionId: String(connection.id),
					},
					search: {},
				}),
			};
		});
	const Browser = section ? ConnectionsSection : ConnectionsBrowser;
	return (
		<Browser
			entries={entries}
			action={
				action ?? (
					<AddConnectionButton
						ownerSlug={ownerSlug}
						connectorKey={connectorKey}
					/>
				)
			}
		/>
	);
}
